import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, Plus, Search } from "lucide-react";
import styles from "@/components/shell/DecisionListVisual.module.css";
import { Button } from "@/components/ax/Button";
import { EmptyAxis } from "@/components/ax/EmptyAxis";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { useFirstView } from "@/hooks/useFirstView";
import { useI18n } from "@/i18n";
import { listDecisions, setChooseBy } from "@/lib/decisionStore";
import { ChooseByControl } from "@/components/workspace/ChooseByControl";
import { canResumeIntake, intakeUrl } from "../lib/intakeResume";

const STATUSES = ["draft", "composing", "ready", "committed", "deferred"];

const Skeleton = () => (
  <div aria-busy="true" data-testid="memory-skeleton" className={styles.cards}>
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className={styles.skeleton}>
        <div className="h-4 w-2/3 rounded-[8px] bg-ax-border" />
        <div className="h-3.5 w-1/3 rounded-[8px] bg-ax-border" />
      </div>
    ))}
  </div>
);

export const StatusTag = ({ status }) => {
  const { t } = useI18n();
  return (
    <span
      data-testid={`status-tag-${status}`}
      className="inline-flex h-6 items-center rounded-[8px] border border-ax-border px-2 font-mono text-[13px] leading-[18px] text-ax-text-2"
    >
      {t(`status.${status}`)}
    </span>
  );
};

export default function Keputusan() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const first = useFirstView("decisions");
  const [state, setState] = useState("loading");
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    const res = await listDecisions();
    if (res.error) setState("error");
    else {
      setList(res.decisions);
      setState("ready");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim().toLowerCase()), 200);
    return () => clearTimeout(id);
  }, [query]);

  const visible = useMemo(
    () =>
      list.filter(
        (d) =>
          (filter === "all" || d.status === filter) &&
          (!debounced ||
            d.title.toLowerCase().includes(debounced) ||
            t(`status.${d.status}`).toLowerCase().includes(debounced)),
      ),
    [list, filter, debounced, t],
  );

  const fmt = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div data-testid="decisions-page" className={styles.page}>
      <p className={styles.eyebrow}>{t("decisions.workspace")}</p>
      <div className={styles.heading}>
        <h1 className={styles.title}>{t("nav.decisions")}</h1>
        {state === "ready" && <span className={styles.count} data-testid="decisions-count">{t("decisions.count", { count: list.length })}</span>}
      </div>
      <p className={styles.lede}>{t("decisions.lede")}</p>

      {state === "loading" && <Skeleton />}

      {state === "error" && (
        <div role="alert" data-testid="memory-error" className={`${first ? "enter " : ""}${styles.error}`}>
          <p className="m-0 text-base text-ax-text2">{t("error.read")}</p>
          <Button onClick={load} data-testid="memory-retry-button" className="mt-4">
            {t("retry")}
          </Button>
        </div>
      )}

      {state === "ready" && list.length === 0 && (
        <section data-testid="memory-empty" className={`${first ? "enter " : ""}${styles.empty}`}>
          <EmptyAxis play={first} />
          <p>{t("mem.empty")}</p>
        </section>
      )}

      {state === "ready" && list.length > 0 && (
        <div className={first ? "enter" : undefined}>
          <div className={styles.toolbar}>
          <label htmlFor="memory-search" className="sr-only">
            {t("mem.search")}
          </label>
          <div className={styles.search}><Search size={18} strokeWidth={1.5} aria-hidden="true" /><input
            id="memory-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("mem.searchPh")}
            data-testid="memory-search-input"
            className="h-12 w-full rounded-[8px] border border-ax-border bg-ax-surface px-3 text-base text-ax-text placeholder:text-ax-muted focus:border-ax-accent focus:outline-none"
          /></div>
          <div role="group" aria-label={t("mem.filters")} className={styles.filters}>
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={filter === s}
                onClick={() => setFilter(s)}
                data-testid={`filter-chip-${s}`}
                className={`min-h-[44px] min-w-[44px] rounded-[8px] border px-3 text-sm font-medium leading-5 transition-colors duration-150 ${
                  filter === s ? "tint-chip-active text-ax-link" : "border-ax-border bg-ax-surface text-ax-text hover:bg-ax-bg"
                }`}
              >
                {s === "all" ? t("filter.all") : t(`status.${s}`)}
              </button>
            ))}
          </div>
          </div>

          {visible.length === 0 ? (
            <p data-testid="memory-empty-filter" className={styles.emptyFilter}>
              {t("mem.emptyFilter")}
            </p>
          ) : (
            <ul className={styles.cards}>
              {visible.map((d) => (
                <li key={d.id} className={styles.card}>
                  <Link
                    to={`/keputusan/${d.id}`}
                    data-testid={`decision-row-${d.id}`}
                    className={styles.cardLink}
                  >
                    <span className={styles.metadata}>
                      <StatusTag status={d.status} />
                      <span>{fmt.format(new Date(d.updatedAt))}</span>
                    </span>
                    <span className={styles.cardTitle}>{d.title}</span>
                    {d.question && <span className={`line-clamp-2 ${styles.question}`}>{d.question}</span>}
                    <span className={styles.footer}>
                      <span className={styles.horizon}><CalendarDays size={14} strokeWidth={1.5} aria-hidden="true" />{t("decisions.horizon", { n: d.horizonDays })}</span>
                      <span className={styles.open}>{t("decisions.open")}<ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" /></span>
                    </span>
                  </Link>
                  {canResumeIntake(d) && <Button
                    onClick={() => navigate(intakeUrl(d.id))}
                    data-testid={`resume-intake-${d.id}`}
                    className="mx-4 mb-4"
                  >{t("intake.resume")}</Button>}
                  <ChooseByControl decision={d} onSave={async (day) => {
                    const res = await setChooseBy(d.id, day);
                    if (res.decision) setList((current) => current.map((entry) => entry.id === d.id ? res.decision : entry));
                    return res;
                  }} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <PrimaryBar>
        <Button
          variant="primary"
          onClick={() => navigate("/keputusan/baru")}
          data-testid="new-decision-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          <Plus size={18} strokeWidth={1.5} aria-hidden="true" />
          {t("decision.new")}
        </Button>
      </PrimaryBar>
    </div>
  );
}
